import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'

import { orderBy, process, SortDescriptor } from '@progress/kendo-data-query'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'

import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { isFailed } from 'redux/toolkit/api'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { AvailableSettings, GeoPolicy, GeoPolicyStatus } from 'types/Settings'
import routesConfig from 'lib/routesConfig'

import { updateAccountSettings, updateDomainSettings } from 'redux/features/settings/settingsSlice'
import { useInboundSettingsRights } from 'components/libs/userRights/pages/useInboundSettingsRights'
import {
  GeoPoliciesForm,
  GeoPoliciesFormState,
  useGeoPoliciesForm
} from 'components/pages/inboundSettings/regionalPolicies/useGeoPoliciesForm'

export interface State {
  isConfirmDialogOpen: boolean
  sort: SortDescriptor[]
  error: string
  policyOptions: string[]
  form: GeoPoliciesForm
  formState: GeoPoliciesFormState
  tableData: {
    total: number
    data: GeoPolicy[]
  }
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (id: string) => void
  onRemoveConfirm: () => void
  onRemoveCancel: () => void
  onBulkEdit: () => void
  handleSortChange: (e: GridSortChangeEvent) => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleOnSelectChange: (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
}

export type UseGeoPoliciesTableLogic = [State, EventHandlers]

export interface UseGeoPoliciesTableLogicProps {
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

export const enum GEO_SELECT_NAMES {
  COUNTRY = 'country',
  POLICY = 'policy'
}

const initialSort = [{ field: GEO_SELECT_NAMES.COUNTRY, dir: 'asc' } as SortDescriptor]

const INITIAL_FORM_STATE = {
  country: 'AFG',
  policy: GeoPolicyStatus.block,
  comment: ''
}

const BLANK_GEO_ENTRY = { ...INITIAL_FORM_STATE, id: '' }

export const useGeoPoliciesTableLogic = ({
  delegateDirtyFormState
}: UseGeoPoliciesTableLogicProps): UseGeoPoliciesTableLogic => {
  const form = useGeoPoliciesForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const dispatch = useAppDispatch()
  const [sort, setSort] = useState(initialSort)
  const [removeId, setRemoveId] = useState<string>('')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false)
  const [presentPolicies, setPresentPolicies] = useState<GeoPolicy[]>([])

  const { accessTokenObject, accountSettings, domainSettings, isCplLoadFailed } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings,
    isCplLoadFailed:
      !!_stores.auth.accessTokenObject?.isCplAccount &&
      (isFailed(_stores.settings.getAccountSettingsApiStatus) || isFailed(_stores.settings.getDomainSettingsApiStatus))
  }))
  const { canQuarantineSettings } = useInboundSettingsRights()

  const tableData = useMemo(() => {
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    const parsedData = settings.geoip_policies ? (JSON.parse(settings.geoip_policies as string) as GeoPolicy[]) : []
    setPresentPolicies(parsedData || [])
    const orderedData = orderBy(parsedData || [], sort)
    const { data } = process(orderedData, {})

    // Have the first item as an edit item.  This method works with sorting.
    data.unshift(BLANK_GEO_ENTRY)
    return {
      data,
      total: data.length || 0
    }
  }, [accessTokenObject?.pdDomainId, domainSettings, accountSettings, sort])

  const updateData = useCallback(
    (policies: GeoPolicy[]) => {
      if (accessTokenObject?.pdDomainId) {
        dispatch(
          updateDomainSettings({
            domainId: accessTokenObject.pdDomainId,
            settings: { [AvailableSettings.GEOIP_POLICIES]: policies }
          })
        )
      } else {
        dispatch(updateAccountSettings({ settings: { [AvailableSettings.GEOIP_POLICIES]: policies } }))
      }
    },
    [dispatch, accessTokenObject]
  )

  const onAdd = useCallback(() => {
    const newPolicy: GeoPolicy = {
      ...form.getState(),
      id: MD5(makeUuid()).toString()
    }
    if (isCplLoadFailed) {
      return
    }
    if (form.validate(newPolicy, { presentPolicies })) {
      const policies = [...presentPolicies, newPolicy]
      updateData(policies)
    }
  }, [form, isCplLoadFailed, presentPolicies, updateData])

  const onRemove = useCallback(
    (id: string) => {
      setRemoveId(id)
      setIsConfirmDialogOpen(true)
    },
    [setRemoveId, setIsConfirmDialogOpen]
  )

  const onRemoveConfirm = useCallback(() => {
    if (!isCplLoadFailed) {
      const policies = presentPolicies.filter(entry => entry.id !== removeId)
      updateData(policies)
    }
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [isCplLoadFailed, presentPolicies, removeId, updateData, setIsConfirmDialogOpen, setRemoveId])

  const onRemoveCancel = useCallback(() => {
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [setRemoveId, setIsConfirmDialogOpen])

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_GEOIP_POLICIES_BULK_EDIT.goto()
  }, [])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      form.fields.comment.setValue(e.target.value)
      form.delegateIsDirty()
    },
    [form]
  )

  const handleOnSelectChange = useCallback(
    (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const { name, value } = e.target
      if (typeof value !== 'string') {
        return
      }
      // eslint-disable-next-line default-case
      switch (true) {
        case name === GEO_SELECT_NAMES.COUNTRY:
          form.setError('')
          form.fields.country.setValue(value)
          break
        case name === GEO_SELECT_NAMES.POLICY:
          form.fields.policy.setValue(value)
          break
      }
      form.delegateIsDirty()
    },
    [form]
  )

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort)
  }, [])

  return useMemo(
    () => [
      {
        isConfirmDialogOpen,
        sort,
        error: form.error,
        policyOptions: canQuarantineSettings ? Object.keys(GeoPolicyStatus) : [GeoPolicyStatus.block],
        form,
        formState: form.getState(),
        tableData
      },
      {
        onAdd,
        onRemove,
        onRemoveConfirm,
        onRemoveCancel,
        onBulkEdit,
        handleSortChange,
        handleOnInputChange,
        handleOnSelectChange
      }
    ],
    [
      isConfirmDialogOpen,
      sort,
      canQuarantineSettings,
      form,
      tableData,
      onAdd,
      onRemove,
      onRemoveConfirm,
      onRemoveCancel,
      onBulkEdit,
      handleSortChange,
      handleOnInputChange,
      handleOnSelectChange
    ]
  )
}
